<template>
  <b-col cols="12">
    <b-row v-if="currentResolution != null">
      <!-- CURRENT RESOLUTION PART -->
      <b-col sm="12" md="12" lg="7" xl="7">
        <b-row class="px-3">
          <b-col cols="12" class="my-3 d-flex">
            <h2 v-if="currentResolution.status != meetingResolutionStatuses.started">
              Résolution présentée
            </h2>
            <h2 v-else>Résolution en cours de vote</h2>

            <h2 v-if="!isChildResolution" class="ml-5">
              <b-badge
                pill
                class="px-3"
                :variant="
                  currentResolution.status != meetingResolutionStatuses.started
                    ? 'primary'
                    : 'success'
                "
              >
                {{ currentResolution.resolution_number }}
              </b-badge>
            </h2>
            <h2 v-else class="ml-5">
              <b-badge
                pill
                class="px-3"
                :variant="
                  currentResolution.status != meetingResolutionStatuses.started
                    ? 'primary'
                    : 'success'
                "
              >
                {{ currentResolution.parent_resolution.resolution_number }}.{{
                  currentResolution.resolution_number
                }}
              </b-badge>
            </h2>
          </b-col>

          <!-- Parent Resolution -->
          <b-col v-if="isChildResolution" cols="12" class="my-3">
            <b-card class="bg-light">
              <div class="mb-3">
                <div>
                  <strong>Titre de la résolution&nbsp;:</strong>
                </div>
                <h2 class="p-1">{{ currentResolution.parent_resolution.name }}</h2>
              </div>
              <div class="mb-3">
                <div>
                  <strong>Description de la résolution&nbsp;:</strong>
                </div>
                <p class="px-2" v-html="showBr(currentResolution.parent_resolution.description)" />
              </div>
              <div v-if="currentResolution.parent_resolution.media.length > 0">
                <h6>
                  <strong>Fichiers :</strong>
                </h6>
                <div v-for="media in currentResolution.parent_resolution.media" :key="media.id">
                  <div
                    class="cursor-pointer"
                    @click="openLink(links.PUBLIC_PATH() + media.file_path)"
                  >
                    {{ media.filename }}
                  </div>
                </div>
              </div>
            </b-card>
          </b-col>

          <b-col cols="12" class="my-3">
            <div>
              <strong>
                Titre de la
                <span v-if="isChildResolution">sous</span> résolution&nbsp;:
              </strong>
            </div>
            <h2 class="p-1">{{ currentResolution.name }}</h2>
          </b-col>
          <b-col cols="12" class="my-3">
            <div>
              <strong>
                Description de la
                <span v-if="isChildResolution">sous</span> résolution&nbsp;:
              </strong>
            </div>
            <p class="px-2" v-html="showBr(currentResolution.description)" />
          </b-col>
          <b-col v-if="!hasMeetingGuest" cols="12" class="my-3 d-flex row">
            <b-col
              v-if="currentResolution.majority || currentResolution.revote_majority"
              class="d-flex flex-row"
            >
              <div v-if="currentResolution.majority">
                <h6>
                  <strong>Majorité&nbsp;:</strong>
                </h6>
                <h6 class="px-1">{{ currentResolution.majority.name }}</h6>
              </div>
              <div v-if="currentResolution.revote_majority" class="ml-5">
                <h6>
                  <strong>Majorité revote&nbsp;:</strong>
                </h6>
                <h6 class="px-1">{{ currentResolution.revote_majority.name }}</h6>
              </div>
            </b-col>
            <b-col v-if="withVote && currentResolution.voting_category" class="">
              <h6>
                <strong>Catégorie de vote&nbsp;:</strong>
              </h6>
              <h6 class="px-1">{{ currentResolution.voting_category.name }}</h6>
            </b-col>
          </b-col>

          <b-col v-if="currentResolution.media.length > 0" cols="12">
            <h6>
              <strong>Fichiers :</strong>
            </h6>
            <div v-for="media in currentResolution.media" :key="media.id">
              <div class="cursor-pointer" @click="openLink(links.PUBLIC_PATH() + media.file_path)">
                {{ media.filename }}
              </div>
            </div>
          </b-col>

          <b-col
            v-if="withVote && (building_owner_id != null || contact_id != null)"
            cols="12"
            class="my-5"
          >
            <b-overlay
              class="lock-votes-overlay"
              bg-color="red"
              opacity="0.5"
              :show="currentResolution.status != meetingResolutionStatuses.started"
              rounded="sm"
              :style="{
                cursor:
                  currentResolution.status != meetingResolutionStatuses.started
                    ? 'not-allowed'
                    : 'unset',
              }"
            >
              <h2>Votes</h2>

              <b-row
                v-for="mandate in allRepresentedMandates"
                :key="`votes-${mandate.id}`"
                class="mx-2 my-5 p-3 card"
                :class="{ 'bg-light': mandate.building_owner_id == building_owner_id }"
              >
                <b-col>
                  <h5>
                    <appMandateName
                      :building-owner-id="mandate.building_owner_id"
                      :mandate-current-tantiemes="mandateCurrentTantiemes(mandate)"
                    />
                  </h5>
                  <hr />
                  <b-row v-if="mandateCurrentTantiemes(mandate) > 0">
                    <b-col>
                      <appVotingCheckboxes
                        :mandate-current-resolution-vote="mandateCurrentResolutionVote(mandate)"
                        @setVote="setVote($event, mandate, mandateCurrentResolutionVote(mandate))"
                      />
                    </b-col>
                  </b-row>
                  <b-row v-else>
                    <b-col>
                      <div class="ml-2">
                        Parce que vous avez 0 tantiemes, vous ne pouvez pas voter sur cette
                        résolution!
                      </div>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
              <template v-slot:overlay>
                <div class="text-center">
                  <font-awesome-icon icon="lock" size="4x" class="text-secondary" />
                </div>
              </template>
            </b-overlay>
          </b-col>
        </b-row>
      </b-col>
      <!-- RESULT PART  -->
      <b-col
        v-if="withVote && !hasMeetingGuest"
        sm="12"
        md="12"
        lg="5"
        xl="5"
        class="mt-md-5 border-left"
      >
        <b-col cols="12" class="my-3">
          <h2 class="">Les personnes qui doivent voter</h2>
          <b-row class="my-2">
            <b-col class="border-right" cols="6">
              <h5 class="my-2">Ils n'ont pas encore voté</h5>
            </b-col>
            <b-col cols="6">
              <h5 class="my-2">Ils ont voté</h5>
            </b-col>
            <b-col class="border-right border-top" cols="6">
              <div v-for="mandate in notVotedMandates" :key="mandate.id" class="my-2">
                <appMandateName
                  :building-owner-id="mandate.building_owner_id"
                  :mandate-current-tantiemes="mandateCurrentTantiemes(mandate)"
                ></appMandateName>
              </div>
            </b-col>
            <b-col class="border-top" cols="6">
              <div v-for="mandate in votedMandates" :key="mandate.id" class="my-2 ">
                <!-- prevent to show mandate with mandatePresenceStatuses.invalid -->
                <appMandateName
                  v-if="mandate.presence_status != mandatePresenceStatuses.invalid"
                  :building-owner-id="mandate.building_owner_id"
                  :mandate-current-tantiemes="mandateCurrentTantiemes(mandate)"
                ></appMandateName>
              </div>
            </b-col>
          </b-row>
          <div style="height: 50px"></div>
          <b-row class="my-5 text-center">
            <b-badge
              v-if="getTextAndBadgeVariantForVotedResolution(currentResolution.status)"
              class="shadow mx-auto"
              :variant="getTextAndBadgeVariantForVotedResolution(currentResolution.status).variant"
            >
              <h2 class="mx-3 my-2">
                {{ getTextAndBadgeVariantForVotedResolution(currentResolution.status).text }}
              </h2></b-badge
            >
          </b-row>
        </b-col>
      </b-col>
    </b-row>
    <b-row v-if="currentResolution == null" class="justify-content-center">
      <b-col cols="12" class="text-center">
        <h3>Il n'y a pas de résolution où le vote a commencé.</h3>
      </b-col>
    </b-row>
  </b-col>
</template>

<script>
import links from "@/links/links";
import MandateORM from "@/store/orm-axios/models/Mandate";
import MeetingORM from "@/store/orm-axios/models/Meeting";
import ResolutionVoteORM from "@/store/orm-axios/models/ResolutionVote";
import MajorityORM from "@/store/orm-axios/models/Majority";
import MeetingDetailsMixin from "@/mixins/meeting-details";

var updateLastSeenInterval;

export default {
  components: {
    appMandateName: () => import("./MandateName"),
    appVotingCheckboxes: () => import("./VotingCheckboxes.vue"),
  },
  mixins: [MeetingDetailsMixin],
  props: {
    currentResolution: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  data() {
    return {
      vote: null,
      links: links,
    };
  },
  computed: {
    withVote() {
      var currentMajority = MajorityORM.find(this.currentResolution.majority_id);
      try {
        if (currentMajority.majority_conditions) {
          return true;
        }
        return false;
      } catch {
        return false;
      }
    },

    isChildResolution() {
      return !!this.currentResolution.parent_resolution_id;
    },

    meeting() {
      return MeetingORM.getters("getMeetingById")(this.$route.params.id);
    },

    allMandates() {
      return MandateORM.getters("getMandatesByMeeting")(this.meeting.id);
    },
    presentInSomeWayMandates() {
      return this.$_.filter(this.allMandates, mandate => {
        // NOTE: to ignore ignoredCorrespondence votes
        if (mandate.presence_status === this.mandatePresenceStatuses.correspondence) {
          var resolutionVote = ResolutionVoteORM.getters("getVoteByMandateAndResolution")(
            mandate.id,
            this.currentResolution.id
          );
          var ignoredCorrespondenceVote = resolutionVote
            ? !!resolutionVote.ignored_correspondence
            : false;
        }

        return (
          mandate.presence_status != null &&
          mandate.presence_status != this.mandatePresenceStatuses.not_present &&
          mandate.presence_status != this.mandatePresenceStatuses.invalid &&
          // NOTE: to ignore ignoredCorrespondence votes
          (mandate.presence_status === this.mandatePresenceStatuses.correspondence
            ? !ignoredCorrespondenceVote
            : true)
        );
      });
    },
    votedMandates() {
      return this.$_.filter(this.presentInSomeWayMandates, mandate => {
        return this.$_.find(this.resolutionVotes, vote => {
          return mandate.id == vote.mandate_id;
        });
      });
    },
    notVotedMandates() {
      return this.$_.filter(this.presentInSomeWayMandates, mandate => {
        return !this.$_.find(this.resolutionVotes, vote => {
          return mandate.id == vote.mandate_id;
        });
      });
    },
    // ..
    mandate() {
      if (this.meeting) {
        return MandateORM.getters("getMandateByMeetingAndBuildingOwner")(
          this.meeting.id,
          this.building_owner_id
        );
      } else {
        return {};
      }
    },
    representedMandates() {
      if (this.meeting) {
        return MandateORM.getters("getMandatesRepresentedByMeeting")(
          this.meeting.id,
          this.building_owner_id,
          this.contact_id
        );
      } else {
        return [];
      }
    },
    allRepresentedMandates() {
      let allRepresentedMandates = [];
      if (this.mandate) allRepresentedMandates.push(this.mandate);
      this.representedMandates.forEach(mandate => {
        allRepresentedMandates.push(mandate);
      });
      return allRepresentedMandates;
    },

    mandateCurrentResolutionVote() {
      return mandate => {
        if (mandate && this.meeting && this.currentResolution) {
          return ResolutionVoteORM.getters("getVoteByMandateAndResolution")(
            mandate.id,
            this.currentResolution.id
          );
        } else {
          return {};
        }
      };
    },
    // NOTE: ignore the case of reduced mandate tantiemes. we don't need to know here the exact tantiemes, only if it has some or not. they won't be reduced to 0 in a real life situation
    mandateCurrentTantiemes() {
      return mandate => {
        if (mandate && this.meeting && this.currentResolution) {
          let votingCategorySlug;
          if (this.currentResolution.voting_category) {
            votingCategorySlug = this.currentResolution.voting_category.slug;
          }

          let buildingOwner = this.findBuildingOwner(mandate.building_owner_id);
          if (votingCategorySlug) {
            return buildingOwner.votingRightsByVotingCategory[votingCategorySlug].tantiemes;
          }
        }
        return 0;
      };
    },

    resolutionVotes() {
      if (this.currentResolution) {
        return ResolutionVoteORM.getters("getVotesThatMeanSomethingByResolution")(
          this.currentResolution.id
        );
      } else {
        return [];
      }
    },
    hasMeetingGuest() {
      return localStorage.meeting_guest_id ? true : false;
    },
  },
  watch: {
    async currentResolution(newVal, oldVal) {
      try {
        if (newVal.id != oldVal.id) {
          this.$store.state.showLoaderOverlay = true;
          ResolutionVoteORM.deleteAll();
          await this.getCurrentResolutionVotes(newVal.id);
          this.$store.state.showLoaderOverlay = false;
        }
      } catch {
        this.$store.state.showLoaderOverlay = false;
      }
    },
  },
  created() {
    console.log("meetingDetails", this.meetingDetails);
  },
  mounted() {
    this.updateLastSeen();
    updateLastSeenInterval = setInterval(() => {
      this.updateLastSeen();
    }, process.env.VUE_APP_UPDATE_LAST_SEEN_INTERVAL);
  },
  beforeDestroy() {
    clearInterval(updateLastSeenInterval);
  },
  methods: {
    updateLastSeen() {
      if (this.$route.name == "meeting-start") {
        var lastSeen = this.$moment.utc().format("HH:mm:ss");
        var mandateToUpdate = this.updateMandateLastSeen(lastSeen);
        var mandatesToUpdate = this.updateRepresentedMandatesLastSeen(lastSeen);

        if (mandateToUpdate) mandatesToUpdate.unshift(mandateToUpdate);
        this.updateMultipleMandatesLastSeen(mandatesToUpdate);
      }
    },
    updateMandateLastSeen(lastSeen) {
      var self = this;
      var mandateToUpdate = null;
      if (this.mandate) {
        mandateToUpdate = MandateORM.find(this.mandate.id);
        mandateToUpdate.showed_up = 1;
        mandateToUpdate.presence_status = self.mandatePresenceStatuses.video;
        mandateToUpdate.last_seen = lastSeen;
      }
      return mandateToUpdate;
    },
    updateRepresentedMandatesLastSeen(lastSeen) {
      var self = this;
      var mandatesToUpdate = [];
      this.representedMandates.forEach(mandate => {
        var mandateToUpdate = MandateORM.find(mandate.id);
        mandateToUpdate.showed_up = 1;
        mandateToUpdate.presence_status = self.mandatePresenceStatuses.pouvoir_by_video;
        mandateToUpdate.last_seen = lastSeen;

        mandatesToUpdate.push(mandateToUpdate);
      });
      return mandatesToUpdate;
    },
    updateMultipleMandatesLastSeen(mandatesToUpdate) {
      MandateORM.api()
        .UPDATE_MULTIPLE_LAST_SEEN(mandatesToUpdate)
        .catch(err => {
          if (this.errorParticipantAlreadyPresentPhysical(err)) {
            this.$fire({
              text: "Access denied",
              title: "ERROR",
              type: "error",
              timer: 2000,
            });
            this.$router.push({
              name: "meetings",
            });
          }
        });
    },
    errorParticipantAlreadyPresentPhysical(err) {
      try {
        return err.response.data["hydra:description"] == "Participant already present physical!";
      } catch {
        return false;
      }
    },

    async setVote(voteType, mandate, currentResolutionVote) {
      this.$store.state.showLoaderOverlay = true;

      var action;
      var voteObj;

      if (currentResolutionVote) {
        action = "PUT";
        if (voteType == currentResolutionVote.vote) {
          voteType = null;
        }
        voteObj = {
          id: currentResolutionVote.id,
          mandate_id: currentResolutionVote.mandate_id,
          meeting_has_resolution_id: currentResolutionVote.meeting_has_resolution_id,
          presence_status: mandate.presence_status,
          vote: voteType,
          agency_id: Number(localStorage.getItem("agencyId")),
        };
        ResolutionVoteORM.update({
          where: voteObj.id,
          data: voteObj,
        });
      } else {
        action = "POST";
        voteObj = {
          mandate_id: mandate.id,
          vote: voteType,
          presence_status: mandate.presence_status,
          meeting_has_resolution_id: this.currentResolution.id,
          agency_id: Number(localStorage.getItem("agencyId")),
        };
      }

      await ResolutionVoteORM.api()
        [action](voteObj, !currentResolutionVote)
        .then(res => {
          console.log("ResolutionVoteORM res :", action, res);
        })
        .catch(err => {
          console.error("ResolutionVoteORM err :", action, err);
          this.$fire(this.alertError());
        });

      this.$store.state.showLoaderOverlay = false;
    },
    async getCurrentResolutionVotes(currentResolutionId) {
      await ResolutionVoteORM.api()
        .FETCH(currentResolutionId)
        .then(res => {
          console.log("RESOLUTION VOTES res:", res);
        })
        .catch(err => {
          this.$fire({
            title: "ERROR",
            type: "error",
            text: err,
          });
          console.error("ResolutionVoteORM.FETCH", err);
        });
    },
    getTextAndBadgeVariantForVotedResolution(status) {
      if (status == this.meetingResolutionStatuses.accepted) {
        return { variant: "success", text: "Accepté" };
      } else if (status == this.meetingResolutionStatuses.rejected) {
        return { variant: "danger", text: "Rejetée" };
      } else if (status == this.meetingResolutionStatuses.sansVote) {
        return { variant: "secondary", text: "Sans vote" };
      } else if (status == this.meetingResolutionStatuses.rejected_lack_of_majority) {
        return { variant: "danger", text: "Rejeté faute de majorité" };
      } else if (status == this.meetingResolutionStatuses.notResolved) {
        return { variant: "dark", text: "Pas résolu" };
      }
    },
    openLink(filePath) {
      window.open(filePath, "_blank");
    },
  },
};
</script>
